import { BASE_PATHS as CONSOLE_BASE_PATHS } from '@cast/console-constants';
import { ORGANIZATION_MANAGEMENT_BASE_PATH } from '@cast/infrastructure-organization';
import { REPORTING_ROUTE_SEGMENTS } from '@cast/shared-trial-ui';

export const BASE_PATHS = {
  clusters: CONSOLE_BASE_PATHS.clusters,
  overview: '/automation/overview',
  accessControl: `${ORGANIZATION_MANAGEMENT_BASE_PATH}/access-control`,
  ssoConnections: `${ORGANIZATION_MANAGEMENT_BASE_PATH}/sso-connections`,
  settings: ORGANIZATION_MANAGEMENT_BASE_PATH,
  externalClusters: '/automation/external-clusters',
  eventLog: '/activity/events',
  dbo: '/dbo',
  cloudVms: '/automation/cloud-vms',
  networkIntelligence: '/automation/network-intelligence',
  notifications: '/activity/notifications',
  apa: '/apa',
  costReport: '/automation/cost-report',
  apiAccess: '/user/api-access',
  onboarding: '/onboarding',
  forbidden: '/forbidden',
} as const;

/** Relative React Router `path` values for top-level OneClick App routes. */
export const APP_ROUTE_PATHS = {
  forbidden: 'forbidden',
  clusters: 'clusters',
  externalClusters: 'automation/external-clusters/:clusterId/*',
  costReport: 'automation/cost-report/*',
  overview: 'automation/overview/*',
  dbo: 'dbo/*',
  cloudVms: 'automation/cloud-vms/*',
  networkIntelligence: 'automation/network-intelligence/*',
  budgets: 'automation/cost-report/budgets/*',
  customDashboards: 'automation/cost-report/dashboards/*',
  apa: 'apa/*',
  notifications: 'activity/notifications/*',
  accessControl: 'organization/management/access-control/*',
  ssoConnections: 'organization/management/sso-connections/*',
  settings: 'organization/management/*',
  enterprise: 'organization/enterprise/:enterpriseId/*',
  eventLog: 'activity/events/*',
  apiAccess: 'user/api-access/*',
  /** Split-me links omit the `/automation` prefix — remap via redirect. */
  legacyExternalClusters: 'external-clusters/*',
  legacyNetworkIntelligence: 'network-intelligence/*',
  legacyOrganizationCostReport: 'organization/cost-report/*',
  /** Old OneClick access-control root — remap to management path. */
  legacyAccessControl: 'access-control/*',
  /** SSO used to live under access-control — remap to management sibling. */
  legacyAccessControlSso:
    'organization/management/access-control/sso-connections/*',
  /** Old OneClick event log under automation — remap to activity path. */
  legacyEventLog: 'automation/event-log/*',
  /** Classic /audit-log — remap to activity events. */
  legacyAuditLog: 'audit-log/*',
  /** Classic /notifications — remap to activity notifications. */
  legacyNotifications: 'notifications/*',
  /** Classic /automation/budgets — OneClick nests budgets under cost-report. */
  legacyBudgets: 'automation/budgets/*',
  /** Classic /automation/cluster-list — OneClick clusters home is /clusters. */
  legacyClusterList: 'automation/cluster-list/*',
  /** Classic /automation/schedules/* — OneClick hosts under management. */
  legacyAutomationSchedules: 'automation/schedules/*',
  /** Classic /settings/{hibernation,rebalancing} — remap to management. */
  legacySettingsSchedules: 'settings/*',
  onboarding: '/onboarding',
} as const;

export const DBO_SEGMENTS = {
  caches: 'caches',
  cloudDatabases: 'cloud-databases',
  performanceAdvisor: 'performance-advisor',
} as const;

const NETWORK_INTELLIGENCE_ROUTE_SEGMENTS = {
  netflow: 'netflow',
  serviceMap: 'service-map',
  serviceMapV2: 'service-map-v2',
} as const;

const CLOUD_VMS_ROUTE_SEGMENTS = {
  instances: 'instances',
} as const;

export const ORG_COST_REPORT_BASE = BASE_PATHS.costReport;

export const ORG_COST_REPORT_PATHS = {
  clusters: `${ORG_COST_REPORT_BASE}/clusters`,
  allocationGroups: `${ORG_COST_REPORT_BASE}/allocation-groups`,
  idleResources: `${ORG_COST_REPORT_BASE}/idle-resources`,
  nodeTemplates: `${ORG_COST_REPORT_BASE}/node-templates`,
  savingsReport: `${ORG_COST_REPORT_BASE}/savings-report`,
  budgets: `${ORG_COST_REPORT_BASE}/budgets`,
  customDashboards: `${ORG_COST_REPORT_BASE}/dashboards`,
} as const;

export const CLUSTER_COST_REPORT_SEGMENTS = {
  cluster: 'cluster',
  workloads: 'workloads',
  namespaces: 'namespaces',
  allocationGroups: 'allocation-groups',
  comparison: 'comparison',
  savingsReport: 'savings-report',
} as const;

export const CLUSTER_COST_REPORT_DEFAULT_SUB_PATHS = {
  computeCost: 'compute-cost',
} as const;

export const CLUSTER_INVENTORY_SEGMENTS = {
  nodes: 'nodes',
  namespaces: 'namespaces',
  allocationGroups: 'allocation-groups',
  workloads: 'workloads',
} as const;

export const buildClusterPath = (clusterId: string): string =>
  `${BASE_PATHS.externalClusters}/${clusterId}`;

export const buildClusterCostReportBasePath = (clusterId: string): string =>
  `${buildClusterPath(clusterId)}/cost-report`;

export const buildClusterInventoryBasePath = (clusterId: string): string =>
  `${buildClusterPath(clusterId)}/inventory`;

export const buildClusterInventoryNodesPath = (clusterId: string): string =>
  `${buildClusterInventoryBasePath(clusterId)}/${
    CLUSTER_INVENTORY_SEGMENTS.nodes
  }`;

export const buildClusterInventoryWorkloadsGpuUtilizationPath = (
  clusterId: string
): string =>
  `${buildClusterInventoryBasePath(clusterId)}/${
    CLUSTER_INVENTORY_SEGMENTS.workloads
  }/list/gpu`;

export const buildClusterAutoscalerPath = (clusterId: string): string =>
  `${buildClusterPath(clusterId)}/autoscaler`;

export const buildClusterWoopPath = (clusterId: string): string =>
  `${buildClusterPath(clusterId)}/workload-optimization`;

export const buildClusterStorageAutoscalerBasePath = (
  clusterId: string
): string => `${buildClusterPath(clusterId)}/storage-autoscaler`;

export const buildClusterSettingsPath = (clusterId: string): string =>
  `${buildClusterPath(clusterId)}/settings`;

export const buildClusterEvictorPath = (clusterId: string): string =>
  `${buildClusterPath(clusterId)}/bin-packing`;

export const buildCloudVmsPath = (): string =>
  `${BASE_PATHS.cloudVms}/${CLOUD_VMS_ROUTE_SEGMENTS.instances}`;

export const buildNetworkIntelligencePath = (
  tab: string = NETWORK_INTELLIGENCE_ROUTE_SEGMENTS.netflow
): string => `${BASE_PATHS.networkIntelligence}/${tab}`;

export const buildClusterVulnerabilitiesPath = (clusterId: string): string =>
  `${buildClusterPath(clusterId)}/vulnerabilities`;

export const buildClusterRebalancerSchedulesPath = (
  clusterId: string
): string => `${buildClusterPath(clusterId)}/rebalance/list/schedule`;

export const buildClusterRebalancingPlansPath = (clusterId: string): string =>
  `${buildClusterPath(clusterId)}/rebalance/list/log`;

export const SETTINGS_ROUTE_SEGMENTS = {
  dashboard: 'dashboard',
  billingReport: 'billing-report',
  featureControl: 'feature-control',
  cloudIntegrations: 'cloud-integrations',
  commitments: 'commitments',
  componentControl: 'component-control',
  permissionsCenter: 'permissions-center',
  priceAdjustments: 'discount-engine',
  clusterHibernation: 'cluster-hibernation',
  scheduledRebalancing: 'scheduled-rebalancing',
} as const;

export const SETTINGS_PATHS = {
  overview: `${BASE_PATHS.settings}/dashboard`,
  platformUsage: `${BASE_PATHS.settings}/billing-report`,
  platformUsageReporting: `${BASE_PATHS.settings}/${REPORTING_ROUTE_SEGMENTS.reporting}`,
  platformUsageReportingPlans: `${BASE_PATHS.settings}/${REPORTING_ROUTE_SEGMENTS.plans}`,
  featureControl: `${BASE_PATHS.settings}/feature-control`,
  cloudIntegrations: `${BASE_PATHS.settings}/cloud-integrations`,
  commitments: `${BASE_PATHS.settings}/commitments`,
  componentControl: `${BASE_PATHS.settings}/component-control`,
  permissionsCenter: `${BASE_PATHS.settings}/permissions-center`,
  priceAdjustments: `${BASE_PATHS.settings}/discount-engine`,
} as const;

export const buildSettingsUrl = (path: string): string =>
  `${BASE_PATHS.settings}/${path}`;

export const OVERVIEW_ROUTE_SEGMENTS = {
  organization: 'organization',
} as const;

export const buildOverviewUrl = (path: string): string =>
  `${BASE_PATHS.overview}/${path}`;

export const ACCESS_CONTROL_ROUTE_SEGMENTS = {
  users: 'users',
  userGroups: 'user-groups',
  serviceAccounts: 'service-accounts',
  roleBindings: 'role-bindings',
  resources: 'resources',
} as const;

export const buildAccessControlUrl = (path: string): string =>
  `${BASE_PATHS.accessControl}/${path}`;
